import { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import compressImage from '../utils/compressImage';
import linkifyText from '../utils/linkifyText';
import ImageLightbox from '../components/common/ImageLightbox';
import './Admin.css';

const TYPING_IDLE_MS = 1500;

function toMillis(ts) {
  return ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0;
}

function AttachIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M16.5 6.5L8.5 14.5C7.4 15.6 7.4 17.4 8.5 18.5C9.6 19.6 11.4 19.6 12.5 18.5L19.5 11.5C21.4 9.6 21.4 6.4 19.5 4.5C17.6 2.6 14.4 2.6 12.5 4.5L5.5 11.5C2.9 14.1 2.9 18.3 5.5 20.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export default function AdminDashboard({ user }) {
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [chatError, setChatError] = useState('');
  const [connectionIssue, setConnectionIssue] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const fileInputRef = useRef(null);

  // Live list of every chat, most recently active first.
  useEffect(() => {
    const q = query(collection(db, 'supportChats'), orderBy('lastMessageAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setConnectionIssue(false);
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setChats(list);
        setActiveId((current) => current || list[0]?.id || null);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Chats listener error:', err);
        setConnectionIssue(true);
      }
    );
    return () => unsub();
  }, []);

  // Live messages for whichever chat is selected.
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return undefined;
    }
    const q = query(
      collection(db, 'supportChats', activeId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Messages listener error:', err);
        setConnectionIssue(true);
      }
    );
    return () => unsub();
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark the selected conversation read (from support's side) whenever
  // it's the one currently open, and again whenever new messages arrive
  // while it stays open.
  useEffect(() => {
    if (!activeId) return;
    setDoc(doc(db, 'supportChats', activeId), { supportLastReadAt: serverTimestamp() }, { merge: true }).catch(
      () => {}
    );
  }, [activeId, messages]);

  const activeChat = chats.find((c) => c.id === activeId) || null;
  const visitorReadAtMillis = toMillis(activeChat?.visitorLastReadAt);

  function setTyping(value) {
    if (!activeId) return;
    if (isTypingRef.current === value) return;
    isTypingRef.current = value;
    setDoc(doc(db, 'supportChats', activeId), { supportTyping: value }, { merge: true }).catch(() => {});
  }

  function handleReplyChange(e) {
    setReply(e.target.value);
    if (!activeId) return;
    setTyping(true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setTyping(false), TYPING_IDLE_MS);
  }

  async function handleReplySend(e) {
    e.preventDefault();
    const text = reply.trim();
    if (!text || !activeId || sending) return;
    // Clear immediately - the input shouldn't sit there waiting on the
    // network before it feels like the message actually sent.
    setReply('');
    clearTimeout(typingTimeoutRef.current);
    setTyping(false);
    setSending(true);
    setChatError('');
    try {
      await addDoc(collection(db, 'supportChats', activeId, 'messages'), {
        sender: 'support',
        text,
        createdAt: serverTimestamp(),
      });
      await setDoc(
        doc(db, 'supportChats', activeId),
        { lastMessageAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Admin reply send failed:', err);
      setChatError('Something went wrong sending that. Please try again.');
    } finally {
      setSending(false);
    }
  }

  async function handleImageSelect(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeId) return;
    setChatError('');
    setUploadingImage(true);
    try {
      const dataUrl = await compressImage(file);
      await addDoc(collection(db, 'supportChats', activeId, 'messages'), {
        sender: 'support',
        type: 'image',
        imageData: dataUrl,
        createdAt: serverTimestamp(),
      });
      await setDoc(
        doc(db, 'supportChats', activeId),
        { lastMessageAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Admin image send failed:', err);
      setChatError(err.message || 'Could not send that image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  }

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="admin-sidebar__header">
          <span>Support inbox</span>
          <button type="button" className="admin-signout" onClick={() => signOut(auth)}>
            Sign out
          </button>
        </div>
        {connectionIssue && (
          <p className="admin-connection-notice">
            Connection hiccup - if the inbox stops updating, try reopening it.
          </p>
        )}
        <div className="admin-sidebar__list">
          {chats.length === 0 && <p className="admin-sidebar__empty">No conversations yet.</p>}
          {chats.map((chat) => (
            <button
              type="button"
              key={chat.id}
              className={`admin-chat-item ${chat.id === activeId ? 'is-active' : ''}`}
              onClick={() => setActiveId(chat.id)}
            >
              <span className="admin-chat-item__subject">
                {chat.fullName ? `${chat.fullName} — ` : ''}
                {chat.subject || 'Support request'}
              </span>
              <span className="admin-chat-item__email">
                {chat.email}
                {chat.visitorTyping && <em className="admin-chat-item__typing"> · typing…</em>}
              </span>
            </button>
          ))}
        </div>
      </aside>

      <section className="admin-conversation">
        {activeChat ? (
          <>
            <header className="admin-conversation__header">
              <div>
                <h2>{activeChat.subject || 'Support request'}</h2>
                <span>
                  {activeChat.fullName ? `${activeChat.fullName} · ` : ''}
                  {activeChat.email}
                </span>
              </div>
            </header>
            <div className="admin-conversation__messages" ref={scrollRef}>
              {messages.map((m) => {
                // Bubble side: anything not explicitly from the visitor
                // (support OR the automated 'system' auto-reply) renders
                // on "our side" - a visitor's own client can't write
                // sender: 'support' (see firestore.rules), but the
                // auto-reply uses sender: 'system' precisely so it can
                // still be written from their session while displaying
                // as if it came from the business, not from them.
                const isFromVisitor = m.sender === 'visitor';
                const isSupportAuthored = m.sender === 'support';
                const createdMillis = toMillis(m.createdAt);
                const isRead =
                  isSupportAuthored && createdMillis > 0 && visitorReadAtMillis >= createdMillis;
                const isImage = m.type === 'image';
                return (
                  <div
                    key={m.id}
                    className={`admin-bubble admin-bubble--${isFromVisitor ? 'visitor' : 'support'} ${
                      isImage ? 'admin-bubble--image' : ''
                    }`}
                  >
                    {isImage ? (
                      brokenImages[m.id] ? (
                        <span className="chat-image-error">⚠ Image failed to load</span>
                      ) : (
                        <button
                          type="button"
                          className="chat-image-wrap"
                          onClick={() => setLightboxSrc(m.imageData)}
                        >
                          <img
                            src={m.imageData}
                            alt="Sent attachment"
                            onError={() => setBrokenImages((prev) => ({ ...prev, [m.id]: true }))}
                          />
                        </button>
                      )
                    ) : (
                      linkifyText(m.text)
                    )}
                    {isSupportAuthored && (
                      <span className={`admin-tick ${isRead ? 'admin-tick--read' : ''}`}>
                        {isRead ? '✓✓' : '✓'}
                      </span>
                    )}
                  </div>
                );
              })}
              {activeChat.visitorTyping && <div className="admin-typing">Visitor is typing…</div>}
            </div>

            {chatError && <p className="admin-chat-error">{chatError}</p>}

            <form className="admin-reply" onSubmit={handleReplySend}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="admin-file-input"
                onChange={handleImageSelect}
              />
              <button
                type="button"
                className="admin-attach-btn"
                aria-label="Attach an image"
                disabled={uploadingImage}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingImage ? <span className="admin-mini-spinner" /> : <AttachIcon />}
              </button>
              <input
                type="text"
                value={reply}
                onChange={handleReplyChange}
                placeholder="Type a reply…"
                aria-label="Type a reply"
              />
              <button type="submit" className="btn btn--solid" disabled={sending}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </form>
          </>
        ) : (
          <div className="admin-conversation__empty">
            Signed in as {user.email}. Select a conversation on the left.
          </div>
        )}
      </section>

      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}